/**
 * Le Rideau Isolant — le comparateur avant / après (bloc `luxe-avant-apres`).
 *
 * Le curseur est un <input type="range"> natif, et c'est tout l'intérêt : au
 * doigt il glisse sans jamais emporter le défilement de la page, il répond aux
 * flèches du clavier et il s'annonce tout seul au lecteur d'écran. Ce fichier
 * ne fait donc que deux choses : reporter sa valeur dans la variable CSS `--x`,
 * et proposer le geste une fois quand l'encart entre à l'écran.
 */

const ETAPES = [72, 30, 50];
const PALIER = 620;

class LuxeAvantApres extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #curseur = null;
  #touche = false;
  /** @type {IntersectionObserver | null} */
  #veille = null;

  connectedCallback() {
    this.#curseur = this.querySelector('input[type="range"]');
    if (!this.#curseur) return;

    this.#curseur.addEventListener('input', this.#surGlissement);
    this.#poser(this.#curseur.value);
    this.#guetter();
  }

  disconnectedCallback() {
    this.#veille?.disconnect();
  }

  /** @param {Event} event */
  #surGlissement = (event) => {
    // La main du visiteur prime sur l'animation d'appel
    this.#touche = true;
    this.classList.remove('is-hint');
    const champ = event.target;
    if (champ instanceof HTMLInputElement) this.#poser(champ.value);
  };

  /** @param {string | number} valeur */
  #poser(valeur) {
    this.style.setProperty('--x', `${valeur}%`);
  }

  /** L'appel ne se joue qu'une fois, et seulement si l'encart est vraiment vu. */
  #guetter() {
    if (this.dataset.hint === 'off' || this.#touche) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver !== 'function') return;

    this.#veille = new IntersectionObserver(
      (entrees) => {
        if (!entrees.some((e) => e.isIntersecting)) return;
        this.#veille?.disconnect();
        this.#veille = null;
        this.#jouerLAppel();
      },
      { threshold: 0.45 }
    );
    this.#veille.observe(this);
  }

  async #jouerLAppel() {
    const curseur = this.#curseur;
    if (!curseur || this.#touche) return;

    this.classList.add('is-hint');
    for (const valeur of ETAPES) {
      if (this.#touche) break;
      curseur.value = String(valeur);
      this.#poser(valeur);
      await new Promise((suite) => setTimeout(suite, PALIER));
    }
    this.classList.remove('is-hint');
  }
}

if (!customElements.get('luxe-avant-apres')) {
  customElements.define('luxe-avant-apres', LuxeAvantApres);
}
